import type {
  TableView,
  TableViewKey,
  TableViewQueryParams,
  TableViewUseGetOptions,
  TableViewUseListProps,
} from "./table-view.type";
import type { ApiPayloadList } from "@/services/api";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { client } from "@/config/client";

import {
  getItemQueryKey,
  getListQueryKey,
  normalizeTableViews,
} from "./table-view.helper";

const fetchUserTableViewsList = async (params?: TableViewQueryParams) => {
  const response = await client.user.tableViews.list(params);
  return response.data as ApiPayloadList<TableView>;
};

export const useListUserTableViews = ({
  params,
  queryKey,
  neverStale = false,
  ...options
}: TableViewUseListProps) => {
  const { data, ...queryResult } = useQuery({
    ...options,
    queryKey: queryKey ?? getListQueryKey(params),
    queryFn: () => fetchUserTableViewsList(params),
    staleTime: neverStale ? Infinity : 0,
    refetchOnWindowFocus: false,
  });

  // Always keep `useMemo` to avoid loop during the "should
  // save url params" process in the `list` module
  const tableViews = useMemo(() => {
    // Always check that `parameters` respect the desired format
    return normalizeTableViews(data?.data ?? []);
  }, [data]);

  return {
    ...queryResult,
    tableViews,
  };
};

export const useGetUserTableView = (
  tableKey: TableViewKey,
  options?: TableViewUseGetOptions
) => {
  const { persist = false, ...queryOptions } = options ?? {};

  const params: TableViewQueryParams = {
    "filter[key]": tableKey,
    "filter[is_default]": true, // keep is_default to ensure uniqueness
  };

  // Override list `queryKey` to represent a single item key and allow
  // us to invalidate the query after updating the related tableView
  const queryKey = getItemQueryKey({ tableKey, params, persist });

  // Here we use the list query instead of get single item to be able
  // to fetch by tableKey (using filter params) because we do not know
  // tableView id in front-end.
  const { tableViews, ...queryResult } = useListUserTableViews({
    ...queryOptions,
    queryKey,
    params,
  });

  return {
    ...queryResult,
    tableView: tableViews[0],
  };
};
